import { inIFrame } from '@thoughtspot/browser-util';
import { containsV1inPath } from '@thoughtspot/env-flags';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
    isTrialVersionEnabled,
} from '@thoughtspot/feature-service';
import { generateUUID } from '@thoughtspot/js-util';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-use';
import { useAppContext } from '/@contexts/global-app-context';
import { useCreatePinboardByNameMutation } from '/@services/get-pinboard-list/get-pinboard-list-service';
import {
    getSubscriptionStatus,
    PurchaseOption,
    SubscriptionStatus,
    SubscriptionType,
    trialDaysLeft,
} from '/@services/system/config-service/session-service';
import { useGetMembersDetails } from '/@services/trial/trial-service';
import { notifyTrialPurchaseCompleteObservable } from '../../teams/teams.util';
import { SpotterOnboardingHomeTab } from './spotter-onboarding-home-tab';
import { TrialInfo, TrialInfoProps } from './trial-info';
import { isOpenedFromSpotterFlag } from './trial-info.util';
import { TrialInfoSparkflowContainer } from './trial-info-sparkflow.container';

export interface TrialInfoContainerProps {
    isReactShellEnabled: boolean;
    isEdgeEnabled: boolean;
}

export const TrialInfoContainer: React.FC<TrialInfoContainerProps> = ({
    isReactShellEnabled = true,
    isEdgeEnabled,
}) => {
    const location = useLocation();

    const { sessionService, metricsService, navService } = useAppContext();
    const [startTime, setStartTime] = React.useState(0);
    const [endTime, setEndTime] = React.useState(0);
    const [percentageDiscount, setDiscountPercentage] = React.useState(0);
    const [discountOfferDuration, setDiscountOfferDuration] = React.useState(0);

    const pathLocation =
        isReactShellEnabled && !containsV1inPath() ? location : window.location;

    const isOpenedFromSpotter =
        localStorage.getItem(isOpenedFromSpotterFlag) === 'true' &&
        ![
            '/edge/',
            'isEdgeOrigin',
            'origin=edge',
            'edgeOnboarding',
        ].some(pattern => pathLocation?.hash?.includes(pattern)) &&
        isFeatureEnabled(FEATURE_FLAG_NAMES.ENABLE_EDGE_RETURN_TO_SPOTTER);
    const isConvAssistEdgeOnboarding = pathLocation?.hash?.includes(
        'edge/onboarding',
    );

    const {
        data: discountOfferData,
        error: membersListError,
        loading,
        refetch,
    } = useGetMembersDetails();

    const [key, setKey] = useState(generateUUID());

    const { _createPinboard } = useCreatePinboardByNameMutation();

    useEffect(() => {
        if (discountOfferData?.getMembersDetails?.discountDetail) {
            setStartTime(
                discountOfferData.getMembersDetails.discountDetail
                    .discountStartTime,
            );
            setEndTime(
                discountOfferData.getMembersDetails.discountDetail
                    .discountEndTime,
            );
            setDiscountPercentage(
                discountOfferData.getMembersDetails.discountDetail
                    .discountPercentage,
            );
            setDiscountOfferDuration(
                discountOfferData.getMembersDetails.discountDetail
                    .discountOfferDuration,
            );
        }
    }, [discountOfferData]);

    const [isTrialInfoVisible, setIsTrialInfoVisible] = useState(true);

    const [purchaseOption, setPurchaseOption] = useState<PurchaseOption>(
        PurchaseOption.BUY_NOW,
    );
    let shouldShowTrialHeader = isTrialInfoVisible;
    if (sessionService.isTeamsEnabled()) {
        shouldShowTrialHeader =
            shouldShowTrialHeader &&
            (sessionService.getSubscriptionType() ===
                SubscriptionType.FREE_TRIAL ||
                sessionService.getSubscriptionType() ===
                    SubscriptionType.DEVELOPER_EDITION ||
                getSubscriptionStatus() === SubscriptionStatus.CANCELED);
    }

    shouldShowTrialHeader =
        shouldShowTrialHeader &&
        isTrialVersionEnabled() &&
        !inIFrame() &&
        trialDaysLeft() >= 0;

    useEffect(() => {
        const subscription = notifyTrialPurchaseCompleteObservable.subscribe(
            () => {
                setIsTrialInfoVisible(
                    sessionService.getSubscriptionType() ===
                        SubscriptionType.FREE_TRIAL ||
                        sessionService.getSubscriptionType() ===
                            SubscriptionType.DEVELOPER_EDITION,
                );
                setKey(generateUUID());
            },
        );
        return () => {
            subscription.unsubscribe();
        };
    }, []);

    const showTrialComponent = shouldShowTrialHeader && !!discountOfferData;

    const trialInfoProps: TrialInfoProps = {
        collectEvent: metricsService.collectEvent,
        updateMixPanelProperty: metricsService.updateMixPanelProperty,
        trialDaysLeft: trialDaysLeft(),
        purchaseOption,
        goToTeamsPlanOptions: navService?.goToTeamsPlanOptions,
        teamsNotEnabled: !sessionService.isTeamsEnabled(),
        isDiscountEnabled: sessionService.isDiscountEnabled(),
        startTime,
        endTime,
        percentageDiscount,
        discountOfferDuration,
        goToAdmin: navService?.goToAdmin,
        expiryTimeStamp: sessionService.trialDaysLeftWrapper(),
        timezone: sessionService.getSessionTimeZone(),
        locale: sessionService.getPreferredLocale(),
        isCopilotEnabled: sessionService.isCopilotEnabled(),
        isSpotterFullExperienceEnabled: sessionService.getIsSpotterFullExperienceEnabled(),
    };

    const trialComponent = (() => {
        if (!showTrialComponent) {
            return null;
        }
        if (sessionService.isSparkFlowEnabled()) {
            return (
                <TrialInfoSparkflowContainer
                    key={key}
                    pathname={pathLocation.pathname}
                    goToUrl={navService?.goToUrl}
                    createPinboard={_createPinboard}
                    {...trialInfoProps}
                />
            );
        }
        return <TrialInfo key={key} {...trialInfoProps} />;
    })();

    const renderContent = () => {
        if (isEdgeEnabled) {
            if (isConvAssistEdgeOnboarding) {
                return null;
            }
            if (isOpenedFromSpotter) {
                return (
                    <Vertical>
                        {trialComponent}
                        <SpotterOnboardingHomeTab
                            collectEvent={metricsService.collectEvent}
                        />
                    </Vertical>
                );
            }
        }

        return trialComponent;
    };

    return renderContent();
};
